/**
 * Admin — Sign-in Methods panel
 *
 * Sits at the top of User Management. Toggles which authentication methods are
 * available to the public, backed by system_settings keys and enforced in
 * AuthController. System admins ALWAYS bypass these gates in the backend, so
 * turning a method off can never lock the administrator out.
 *
 * Controls:
 *   - Google                       → auth.google_enabled
 *   - Email & Password (master)    → auth.email_password_enabled
 *       └ Public registration      → allow_public_registration (invite-only when off)
 *   - Apple (placeholder, locked)  → auth.apple_enabled (off; "Coming soon")
 *
 * System-admin UI: English only, light-theme palette matching the admin console.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getSystemSetting, putSystemSetting, SETTING_KEYS } from '../../api/systemSettings';
import styles from './SignInMethodsPanel.module.css';

interface ToggleProps {
  on: boolean;
  disabled?: boolean;
  busy?: boolean;
  onClick?: () => void;
  ariaLabel: string;
}

function Toggle({ on, disabled, busy, onClick, ariaLabel }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={disabled || busy}
      onClick={onClick}
      className={`${styles.toggle} ${on ? styles.toggleOn : ''} ${disabled ? styles.toggleDisabled : ''}`}
    >
      <span className={styles.knob} />
    </button>
  );
}

export default function SignInMethodsPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [google, setGoogle] = useState(true);
  const [email, setEmail] = useState(true);
  const [apple, setApple] = useState(false);
  const [publicReg, setPublicReg] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [g, e, a, p] = await Promise.all([
          getSystemSetting(SETTING_KEYS.AUTH_GOOGLE_ENABLED),
          getSystemSetting(SETTING_KEYS.AUTH_EMAIL_PASSWORD_ENABLED),
          getSystemSetting(SETTING_KEYS.AUTH_APPLE_ENABLED),
          getSystemSetting(SETTING_KEYS.ALLOW_PUBLIC_REGISTRATION),
        ]);
        if (cancelled) return;
        // Defaults: Google + Email ON, Apple OFF, public registration OFF.
        setGoogle(g === null ? true : g === 'true');
        setEmail(e === null ? true : e === 'true');
        setApple(a === null ? false : a === 'true');
        setPublicReg(p === 'true');
      } catch {
        if (!cancelled) setError('Could not load sign-in settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(
    async (key: string, next: boolean, set: (v: boolean) => void, prev: boolean) => {
      setError(null);
      setSaving(key);
      set(next); // optimistic
      try {
        await putSystemSetting(key, next ? 'true' : 'false');
      } catch {
        set(prev); // revert
        setError('Could not save that change. Please try again.');
      } finally {
        setSaving(null);
      }
    },
    [],
  );

  const LAST_METHOD_MSG =
    'Keep at least one public sign-in method on. You, as a system admin, can always sign in regardless.';

  const toggleGoogle = () => {
    if (google && !email) {
      setNotice(LAST_METHOD_MSG);
      return;
    }
    setNotice(null);
    persist(SETTING_KEYS.AUTH_GOOGLE_ENABLED, !google, setGoogle, google);
  };

  const toggleEmail = () => {
    if (email && !google) {
      setNotice(LAST_METHOD_MSG);
      return;
    }
    setNotice(null);
    persist(SETTING_KEYS.AUTH_EMAIL_PASSWORD_ENABLED, !email, setEmail, email);
  };

  const togglePublicReg = () => {
    setNotice(null);
    persist(SETTING_KEYS.ALLOW_PUBLIC_REGISTRATION, !publicReg, setPublicReg, publicReg);
  };

  return (
    <section className={styles.card} aria-label="Sign-in methods">
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>Sign-in Methods</h3>
          <p className={styles.cardSubtitle}>
            Control how people can sign in. System admins always bypass these — you can&rsquo;t lock yourself out.
          </p>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.rows}>
          {/* Google */}
          <div className={styles.row}>
            <div className={styles.rowText}>
              <span className={styles.rowLabel}>Google</span>
              <span className={styles.rowDesc}>Sign in with a Google account (allowlist still applies).</span>
            </div>
            <Toggle
              on={google}
              busy={saving === SETTING_KEYS.AUTH_GOOGLE_ENABLED}
              onClick={toggleGoogle}
              ariaLabel="Toggle Google sign-in"
            />
          </div>

          {/* Email & Password (master) */}
          <div className={styles.row}>
            <div className={styles.rowText}>
              <span className={styles.rowLabel}>Email &amp; Password</span>
              <span className={styles.rowDesc}>Email/password sign-in and registration.</span>
            </div>
            <Toggle
              on={email}
              busy={saving === SETTING_KEYS.AUTH_EMAIL_PASSWORD_ENABLED}
              onClick={toggleEmail}
              ariaLabel="Toggle email and password sign-in"
            />
          </div>

          {/* Public registration (nested under Email) */}
          <div className={`${styles.row} ${styles.rowNested}`}>
            <div className={styles.rowText}>
              <span className={styles.rowLabel}>Public registration</span>
              <span className={styles.rowDesc}>
                {publicReg
                  ? 'Anyone can create an account.'
                  : 'Invite-only — only allowlisted emails can register.'}
              </span>
            </div>
            <Toggle
              on={publicReg}
              disabled={!email}
              busy={saving === SETTING_KEYS.ALLOW_PUBLIC_REGISTRATION}
              onClick={togglePublicReg}
              ariaLabel="Toggle public registration"
            />
          </div>

          {/* Apple (placeholder) */}
          <div className={`${styles.row} ${styles.rowMuted}`}>
            <div className={styles.rowText}>
              <span className={styles.rowLabel}>
                Apple <span className={styles.soon}>Coming soon</span>
              </span>
              <span className={styles.rowDesc}>Sign in with Apple isn&rsquo;t available yet.</span>
            </div>
            <Toggle on={apple} disabled ariaLabel="Apple sign-in (coming soon)" />
          </div>
        </div>
      )}

      {notice && <div className={styles.notice}>{notice}</div>}
      {error && <div className={styles.error}>{error}</div>}
    </section>
  );
}
